// client/src/components/BidEstimation/VendorQuotes/CatalogMatchDropdown.js
import React, { useState, useEffect } from 'react'
import { TextField, Typography, Chip, Box } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import axios from 'axios'

const scoreColor = (score) => {
  if (score >= 0.85) return '#4caf50'
  if (score >= 0.50) return '#ff9800'
  return '#f44336'
}

const SOURCE_BADGE = {
  catalog:   { label: 'Catalog',   bg: '#e8f5e9', color: '#2e7d32' },
  aspire:    { label: 'Aspire',    bg: '#e3f2fd', color: '#1565c0' },
  vendor:    { label: 'Vendor',    bg: '#f3e5f5', color: '#7b1fa2' },
  aggregate: { label: 'Aggregate', bg: '#fff3e0', color: '#e65100' },
  legacy:    { label: 'Legacy',    bg: '#f5f5f5', color: '#666' }
}

export default function CatalogMatchDropdown ({ value, onChange, lineItem, matchCandidates = [], skuSuggestion = null }) {
  const [options, setOptions] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [loading, setLoading] = useState(false)

  // Build initial suggestions from matchCandidates + skuSuggestion
  const suggestions = React.useMemo(() => {
    const items = []

    // SKU suggestion from prior quotes goes first
    if (skuSuggestion) {
      items.push({
        ...skuSuggestion,
        _suggestionType: 'prior',
        score: 1.0
      })
    }

    // Then match candidates from auto-match
    for (const c of matchCandidates) {
      if (c.score < 0.3) continue
      // Skip if same as SKU suggestion
      if (skuSuggestion && String(c.aspireItemId) === String(skuSuggestion.aspireItemId)) continue
      items.push({ ...c, _suggestionType: 'fuzzy' })
    }

    return items
  }, [matchCandidates, skuSuggestion])

  // No auto-search on mount — auto-match already populated matchCandidates server-side.
  // User can type to search the unified catalog on demand.

  // Debounced catalog search
  useEffect(() => {
    if (inputValue.length < 2) {
      setOptions([])
      return
    }
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await axios.get('/api/bid-estimation/unified-catalog', {
          params: { search: inputValue, limit: 25 }
        })
        setOptions(data)
      } catch (e) {
        console.error('Catalog search failed:', e)
      }
      setLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [inputValue])

  // Merge suggestions + search results, deduplicating by aspireItemId
  const allOptions = React.useMemo(() => {
    const seen = new Set()
    const merged = []

    // Suggestions first (from auto-match candidates)
    for (const s of suggestions) {
      const key = String(s.aspireItemId || s._id)
      if (!seen.has(key)) {
        seen.add(key)
        merged.push({ ...s, _fromSuggestions: true })
      }
    }

    // Unified search results after — normalize to common shape
    for (const o of options) {
      const key = String(o.aspireItemId || o.materialCatalogItemId || o.vendorCatalogProductId || o._id)
      if (!seen.has(key)) {
        seen.add(key)
        merged.push({
          ...o,
          aspireItemId: o.aspireItemId || null,
          itemName: o.canonicalName || o.itemName || '',
          unitTypeName: o.unit || o.unitTypeName || '',
          categoryName: o.category || o.categoryName || '',
          bestPrice: o.bestPrice,
          _source: o.source || null,
          _vendorName: o.vendorName || null
        })
      }
    }

    return merged
  }, [suggestions, options])

  const handleCreateNew = async () => {
    const key = `${lineItem.description}||${lineItem.uom}`
    try {
      const { data } = await axios.post('/api/bid-estimation/materials', {
        description: lineItem.description,
        unit: lineItem.uom,
        key,
        category: 'general'
      })
      onChange(data._id, 'new_created')
    } catch (e) {
      if (e.response?.status === 500 && e.response?.data?.error?.includes('duplicate key')) {
        try {
          const { data } = await axios.get('/api/bid-estimation/materials', {
            params: { search: lineItem.description }
          })
          const existing = data.find(item => item.key === key)
          if (existing) {
            onChange(existing._id, 'mapped')
            return
          }
        } catch (searchErr) { /* fall through */ }
      }
      console.error('Create catalog item failed:', e)
    }
  }

  return (
    <Autocomplete
      size='small'
      options={allOptions}
      loading={loading}
      getOptionLabel={(opt) => opt.itemName || ''}
      inputValue={inputValue}
      onInputChange={(e, val) => setInputValue(val)}
      onChange={(e, selected) => {
        if (selected) {
          const catId = selected.aspireItemId || selected.materialCatalogItemId
          if (catId) {
            onChange(catId, 'mapped')
          } else {
            console.warn('Selected item has no catalog ID — cannot map directly:', selected.itemName)
          }
        }
      }}
      renderOption={(opt) => {
        const badge = opt._source ? SOURCE_BADGE[opt._source] : null
        const badgeLabel = opt._source === 'vendor' && opt._vendorName
          ? opt._vendorName
          : badge?.label
        return (
          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
            <Box style={{ minWidth: 0, flex: 1 }}>
              <Box display='flex' alignItems='center' style={{ gap: 6 }}>
                {badge && (
                  <Chip size='small' label={badgeLabel} style={{ backgroundColor: badge.bg, color: badge.color, height: 18, fontSize: 9, flexShrink: 0 }} />
                )}
                <span style={{ fontSize: 12 }}>{opt.itemName}</span>
                {opt._suggestionType === 'prior' && (
                  <Chip size='small' label='Prior match' style={{ backgroundColor: '#2196f3', color: '#fff', height: 18, fontSize: 10 }} />
                )}
                {opt._suggestionType === 'fuzzy' && opt.score != null && (
                  <Chip size='small' label={`${Math.round(opt.score * 100)}%`} style={{ backgroundColor: scoreColor(opt.score), color: '#fff', height: 18, fontSize: 10 }} />
                )}
              </Box>
              <Typography variant='caption' style={{ color: '#777', fontSize: 10 }}>
                {[opt.categoryName, opt.unitTypeName, opt.bestPrice != null ? `$${Number(opt.bestPrice).toFixed(2)}` : ''].filter(Boolean).join(' \u00b7 ')}
              </Typography>
            </Box>
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder='Search catalog...' variant='outlined' size='small' />
      )}
      noOptionsText={
        inputValue.length >= 2 ? (
          <Box onClick={handleCreateNew} style={{ cursor: 'pointer', padding: '6px 16px' }}>
            <Typography color='primary'>+ Create New Catalog Item</Typography>
          </Box>
        ) : (
          suggestions.length === 0 ? 'Type to search...' : 'No strong matches — type to search or create new'
        )
      }
      style={{ minWidth: 250 }}
    />
  )
}
